import { Avatar, Breadcrumbs } from "@heroui/react";
import { AppLayout } from "@heroui-pro/react/app-layout";
import { Navbar } from "@heroui-pro/react/navbar";
import { Sidebar } from "@heroui-pro/react/sidebar";
import {
  Book,
  ChartBar,
  ChartColumn,
  ClockArrowRotateLeft,
  Filmstrip,
  Flask,
  Funnel,
  House,
  LayoutCellsLarge,
  MagicWand,
  Megaphone,
  Picture,
  PlanetEarth,
  Puzzle,
  Rocket,
  Sparkles,
  ListOl,
  Video,
} from "@gravity-ui/icons";

import {
  type ComponentType,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Link, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";

import { useAdminAccess } from "@src/auth/adminAccess";
import { DEV_BYPASS } from "@src/auth/devBypass";
import { NavbarActionsContext } from "@src/layout/NavbarActionsContext";
import { LogoutButton } from "@src/auth/LogoutButton";
import { resolveCurrentUser } from "@src/layout/resolveCurrentUser";

type IconComponent = ComponentType<Record<string, unknown>>;

type SidebarRoute = {
  readonly href: string;
  readonly label: string;
  readonly icon: IconComponent;
  readonly forceReload?: boolean;
};

type NavSection = {
  readonly label: string;
  readonly routes: ReadonlyArray<SidebarRoute>;
};

/* ── Navigation structure ──────────────────────────────────────────── */

const NAV_SECTIONS: ReadonlyArray<NavSection> = [
  {
    label: "Onboarding",
    routes: [
      { href: "/", label: "Overview", icon: House as IconComponent },
      { href: "/funnels", label: "Funnels", icon: Funnel as IconComponent },
      { href: "/experiments", label: "Experiments", icon: Flask as IconComponent },
    ],
  },
  {
    label: "Analytics",
    routes: [
      { href: "/step-analytics", label: "Step analytics", icon: ListOl as IconComponent },
      { href: "/dashboard", label: "Live dashboard", icon: ChartColumn as IconComponent },
      { href: "/reports", label: "Conversion report", icon: ChartBar as IconComponent },
      { href: "/agent-inbox", label: "Agent inbox", icon: Sparkles as IconComponent },
      { href: "/ai-memory", label: "AI memory", icon: Book as IconComponent },
      { href: "/ad-spend", label: "Ad spend", icon: PlanetEarth as IconComponent },
      { href: "/optimization", label: "Campaign optimizer", icon: Rocket as IconComponent },
    ],
  },
  {
    label: "Content",
    routes: [
      { href: "/assets", label: "Assets", icon: Picture as IconComponent },
      { href: "/ad-creatives", label: "Creatives", icon: Video as IconComponent },
      { href: "/bruce", label: "Bruce", icon: Filmstrip as IconComponent },
      { href: "/generate", label: "Generate", icon: MagicWand as IconComponent },
      { href: "/step-templates", label: "Templates", icon: Puzzle as IconComponent },
    ],
  },
  {
    label: "Configuration",
    routes: [
      { href: "/applications", label: "Applications", icon: LayoutCellsLarge as IconComponent },
      { href: "/inlets", label: "Inlets", icon: Megaphone as IconComponent },
      { href: "/audit-log", label: "Audit log", icon: ClockArrowRotateLeft as IconComponent },
      { href: "/storybook/", label: "Storybook", icon: Book as IconComponent, forceReload: true },
    ],
  },
];

const isRouteActive = (pathname: string, href: string): boolean => {
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(`${href}/`);
};

/* ── Right-click–friendly link wrapper ─────────────────────────────── */

/**
 * Renders an `<a>` (via React Router `<Link>`) around the entire menu-item
 * content. Normal clicks still use SPA navigation through the `navigate`
 * callback on `AppLayout`, while right-click / Cmd-click open in a new tab
 * because a real `<a>` element is present.
 */
const NavLink = ({
  to,
  children,
}: {
  readonly to: string;
  readonly children: ReactNode;
}): ReactNode => {
  const handleClick = useCallback((e: MouseEvent) => {
    if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey && e.button === 0) {
      // Plain click — prevent Link navigation; SidebarMenuItem onAction handles SPA routing.
      e.preventDefault();
    } else {
      // Modifier-click — let the <a> open a new tab, but stop propagation so
      // React Aria's press handler on the parent MenuItem doesn't also SPA-navigate.
      e.stopPropagation();
    }
  }, []);

  return (
    <Link
      to={to}
      onClick={handleClick}
      className="flex w-full items-center gap-2 no-underline [color:inherit]"
      tabIndex={-1}
    >
      {children}
    </Link>
  );
};

/* ── Section menu item renderer ─────────────────────────────────────── */

const SectionMenuItem = ({
  route,
  pathname,
}: {
  readonly route: SidebarRoute;
  readonly pathname: string;
}): ReactNode => (
  <Sidebar.MenuItem
    href={route.href}
    isCurrent={isRouteActive(pathname, route.href)}
    {...(route.forceReload ? { forceReload: true } : {})}
  >
    <NavLink to={route.href}>
      <Sidebar.MenuIcon>
        <route.icon />
      </Sidebar.MenuIcon>
      <Sidebar.MenuLabel>{route.label}</Sidebar.MenuLabel>
    </NavLink>
  </Sidebar.MenuItem>
);

/* ── Breadcrumb resolver ────────────────────────────────────────────── */

const useBreadcrumbs = (): { section: string; page: string } | null => {
  const { pathname } = useLocation();
  return useMemo(() => {
    for (const section of NAV_SECTIONS) {
      for (const route of section.routes) {
        if (isRouteActive(pathname, route.href)) {
          return { section: section.label, page: route.label };
        }
      }
    }
    return null;
  }, [pathname]);
};

/* ── Sidebar navigation ────────────────────────────────────────────── */

const SidebarNav = (): ReactNode => {
  const location = useLocation();
  const accessRole = useAdminAccess();
  const sections = useMemo(
    () =>
      accessRole === "uploader"
        ? NAV_SECTIONS.map((section) => ({
            ...section,
            routes: section.routes.filter((route) => route.href === "/bruce"),
          })).filter((section) => section.routes.length > 0)
        : NAV_SECTIONS,
    [accessRole],
  );
  return (
    <>
      <Sidebar.Header>
        <div className="flex items-center gap-2 px-2 py-1">
          <Avatar size="sm" className="bg-primary text-primary-foreground shrink-0">
            <Avatar.Fallback>N</Avatar.Fallback>
          </Avatar>
          <span className="text-sm font-semibold">NextDay AI</span>
          <svg
            className="text-default-400 ml-auto size-3"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 5l3 3 3-3" />
          </svg>
        </div>
      </Sidebar.Header>
      <Sidebar.Content>
        {sections.map((section) => (
          <Sidebar.Group key={section.label}>
            <Sidebar.GroupLabel>{section.label}</Sidebar.GroupLabel>
            <Sidebar.Menu>
              {section.routes.map((route) => (
                <SectionMenuItem key={route.href} route={route} pathname={location.pathname} />
              ))}
            </Sidebar.Menu>
          </Sidebar.Group>
        ))}
      </Sidebar.Content>
      <Sidebar.Footer>
        <CurrentUserCard />
      </Sidebar.Footer>
    </>
  );
};

const providerLabel = (provider: string): string => {
  switch (provider) {
    case "kinde":
      return "via Kinde";
    case "cloudflare_access":
      return "via Cloudflare Access";
    default:
      return "local dev";
  }
};

const CurrentUserCard = (): ReactNode => {
  const user = resolveCurrentUser();
  return (
    <div className="flex items-center gap-2 px-2 py-2">
      <Avatar size="sm">
        <Avatar.Fallback>{user.initials}</Avatar.Fallback>
      </Avatar>
      <div className="flex min-w-0 flex-col">
        <span className="text-foreground truncate text-sm">{user.email}</span>
        <div className="flex items-center gap-2">
          <span className="text-muted text-xs">{providerLabel(user.provider)}</span>
          {!DEV_BYPASS && <LogoutButton />}
        </div>
      </div>
    </div>
  );
};

const ShellNavbar = ({
  onActionsRef,
}: {
  readonly onActionsRef: (el: HTMLElement | null) => void;
}): ReactNode => {
  const crumbs = useBreadcrumbs();
  return (
    <Navbar maxWidth="full">
      <Navbar.Header>
        <AppLayout.MenuToggle />
        <Sidebar.Trigger />
        {crumbs ? (
          <Breadcrumbs>
            <Breadcrumbs.Item>{crumbs.section}</Breadcrumbs.Item>
            <Breadcrumbs.Item>{crumbs.page}</Breadcrumbs.Item>
          </Breadcrumbs>
        ) : null}
        <Navbar.Spacer />
        <div ref={onActionsRef} className="flex items-center gap-2" />
      </Navbar.Header>
    </Navbar>
  );
};

export const AppShell = (): ReactNode => {
  const [actionsEl, setActionsEl] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const accessRole = useAdminAccess();
  // Uploaders own the Bruce library and nothing else.
  if (accessRole === "uploader" && !isRouteActive(pathname, "/bruce")) {
    return <Navigate to="/bruce" replace />;
  }
  const handleNavigate = (href: string): void => {
    if (href.startsWith("/storybook")) {
      window.location.href = href;
      return;
    }
    void navigate(href);
  };
  return (
    <NavbarActionsContext.Provider value={actionsEl}>
      <AppLayout
        navigate={handleNavigate}
        sidebar={
          <>
            <Sidebar>
              <SidebarNav />
            </Sidebar>
            <Sidebar.Mobile>
              <SidebarNav />
            </Sidebar.Mobile>
          </>
        }
        navbar={<ShellNavbar onActionsRef={setActionsEl} />}
      >
        <Outlet />
      </AppLayout>
    </NavbarActionsContext.Provider>
  );
};
